"use client";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import { CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { CheckCircle2, Mail, Loader2 } from "lucide-react";
import { AxiosError } from "axios";
import { authService } from "@/services/auth-service";
import { cn } from "@/lib/utils";

interface FormErrors {
  email?: string;
  general?: string;
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    setErrors({});
    try {
      const { data } = await authService.forgotPassword({ email: email.trim() });
      const message = (data as { message?: unknown })?.message;
      setSuccessNotice(typeof message === "string" && message.length > 0 ? message : null);
      setSubmitted(true);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.error("Axios Error: ", error.response?.data);
      } else {
        console.error("Unknown Error: ", error);
      }

      const newErrors: FormErrors = {};

      if (error instanceof AxiosError && error.response?.data) {
        const data = error.response.data as {
          detail?: unknown;
          message?: string;
        };

        if (Array.isArray(data.detail)) {
          const bannerParts: string[] = [];
          for (const err of data.detail as { loc?: unknown[]; msg?: string }[]) {
            const msg = typeof err.msg === "string" ? err.msg.trim() : "";
            if (!msg) continue;
            const loc = Array.isArray(err.loc) ? err.loc : [];
            const last = loc[loc.length - 1];
            const fieldName = typeof last === "string" ? last : String(last);
            if (fieldName === "email") {
              newErrors.email = newErrors.email ? `${newErrors.email} ${msg}` : msg;
            } else {
              bannerParts.push(msg);
            }
          }
          if (bannerParts.length) {
            newErrors.general = bannerParts.join(" ");
          }
        } else if (typeof data.detail === "string") {
          newErrors.general = data.detail;
        }

        if (!newErrors.email && !newErrors.general && typeof data.message === "string") {
          newErrors.general = data.message;
        }
      }

      if (!newErrors.email && !newErrors.general) {
        const raw =
          error instanceof AxiosError ? error.message : "Something went wrong.";
        newErrors.general = raw.startsWith("Request failed with status code")
          ? "Something went wrong. Please try again."
          : raw;
      }

      setErrors(newErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="relative w-full max-w-[440px]">
        <CardHeader className="mb-8 space-y-0 p-0 text-center">
          <div className="mx-auto mb-2 flex size-14 items-center justify-center rounded-xl bg-violet-50 text-primary">
            <Mail className="size-7" aria-hidden />
          </div>
          <h2 className="mb-2 text-[32px] font-extrabold leading-tight tracking-tight text-foreground">
            Check your email
          </h2>
        </CardHeader>

        <div className="flex gap-3 rounded-xl border border-authBorder bg-violet-50 p-4 text-sm leading-relaxed text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <p>
            {successNotice ??
              "If an account exists for that email, we've sent a link to reset your password."}
          </p>
        </div>

        <div className="mt-2 flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-full border-authBorder text-sm font-semibold text-muted-foreground hover:border-violet-200 hover:bg-violet-50 hover:text-foreground"
            onClick={() => {
              setSubmitted(false);
              setSuccessNotice(null);
              setErrors({});
            }}
          >
            Use a different email
          </Button>
        </div>

        <div className="mt-4 flex justify-center">
          <Button type="button" variant="link" className="px-0 text-sm font-semibold text-primary underline-offset-2" asChild>
            <Link href="/login">Back to login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-[440px]">
      <CardHeader className="mb-8 space-y-0 p-0">
        <h1 className="mb-2 text-[32px] font-extrabold leading-tight tracking-tight text-foreground">
          Forgot password
        </h1>
        <p className="max-w-[420px] text-[15px] text-muted-foreground">
          Enter your account email and we&apos;ll send you a link to reset your password.
        </p>
      </CardHeader>

      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-[18px] space-y-1.5">
          <Label htmlFor="forgot-password-email" className="text-[13px] font-semibold text-foreground">
            Email
          </Label>
          <Input
            id="forgot-password-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({
                ...prev,
                email: undefined,
                general: undefined,
              }));
            }}
            className={cn(
              "h-12 rounded-xl border-authBorder bg-white px-3.5 text-base text-neutral-900 shadow-none placeholder:text-muted-foreground hover:border-violet-200 focus-visible:border-primary focus-visible:ring-primary/20",
              errors.email && "border-destructive focus-visible:ring-destructive/20"
            )}
          />
          {errors.email && (
            <p className="text-[13px] leading-snug text-destructive">{errors.email}</p>
          )}
        </div>

        {errors.general && (
          <p
            className="mb-[18px] rounded-xl bg-destructive/10 p-2.5 text-[13px] leading-snug text-destructive"
            role="alert"
          >
            {errors.general}
          </p>
        )}

        <Button
          type="submit"
          className="mt-2 h-[52px] w-full rounded-full text-[15px] font-bold shadow-cta transition-all hover:-translate-y-px hover:bg-primaryHover hover:shadow-cta-hover"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            "Send reset link"
          )}
        </Button>

        <div className="mt-4 flex justify-center">
          <Button type="button" variant="link" className="px-0 text-sm font-semibold text-primary underline-offset-2" asChild>
            <Link href="/login">Back to login</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
